import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import MainPageLayout from "../../../components/header/MainPageLayout";
import Table from "../../../components/table/Table";
import Pill from "../components/Pill";
import {
    TableCard,
    PageTitleSection,
    PageTitle,
    ActionsCard,
    ActionButtons,
    CreateButtons,
} from "./InstitutionsTable.styles";
import {
    GetAllInstitutionsType,
    Institution,
} from "../../../core/ServerApiLayer/VOne/institutions/get_all_instititions";
import { getJwt } from "../../../core/LocalStorage/AuthSession/core";
import BackButton from "../../../components/button/BackButton/BackButton";
import CreateInstitutionModal from "../../../components/modal/create-institution-modal/CreateInstitutionModal";
import { CreateInstitutionType } from "../../../core/ServerApiLayer/VOne/institutions/create";

interface InstitutionTableProps {
    getAllInstitutions: GetAllInstitutionsType;
    createInstitution: CreateInstitutionType;
}

export const InstitutionTable: React.FC<InstitutionTableProps> = ({
    getAllInstitutions,
    createInstitution,
}) => {
    const navigate = useNavigate(); // Move useNavigate inside the component

    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [loading, setLoading] = useState(true);

    // create institution form data
    const [formData, setFormData] = useState({
        name: "",
    });

    // State to open and close the create modal
    const [openCreateInstitutionModal, setOpenCreateInstitutionModal] =
        useState(false);

    // The list endpoint is the only source of a full institution row.
    const loadInstitutions = useCallback(async () => {
        const jwt = getJwt();
        if (!jwt) {
            console.error("JWT expired or missing.");
            return;
        }

        // The API layer answers a network failure with a 500 rather than a
        // rejection, so reaching the catch means something unforeseen. The
        // table keeps whatever it already had.
        try {
            const response = await getAllInstitutions(jwt);
            if (response.status === 200 && response.data) {
                setInstitutions(response.data);
            }
        } catch (error) {
            console.error("Error loading institutions:", error);
        }
    }, [getAllInstitutions]);

    useEffect(() => {
        loadInstitutions().finally(() => setLoading(false));
    }, [loadInstitutions]);

    const columns = [
        {
            key: "name",
            label: "Institution Name",
        },
        {
            key: "status",
            label: "Status",
            render: (row: Institution) => <Pill>{row.status}</Pill>,
        },
        {
            key: "no_of_licenses",
            label: "No. of Licenses",
        },
        {
            key: "no_of_users",
            label: "No. of Active Users",
        },
        {
            key: "actions",
            label: "Actions",
            render: (row: Institution) => (
                <ActionsCard>
                    <ActionButtons
                        backgroundColorHex="#135185"
                        onClick={() =>
                            navigate(`/institution/${row.entity_id}`)
                        }
                    >
                        View
                    </ActionButtons>
                </ActionsCard>
            ),
        },
    ];

    const onRequestClose = () => {
        setOpenCreateInstitutionModal(false);
        clearFormData();
    };

    const clearFormData = () => {
        //Clear modal after submission
        setFormData({ name: "" });
    };

    const handleCreateInstitution = async (e: any) => {
        e.preventDefault();

        const jwt = getJwt();
        if (!jwt) {
            console.error("JWT expired or missing.");
            return;
        }

        const response = await createInstitution(jwt, formData.name);

        if (response.status === 201 && response.institution) {
            // The create response is the inserted row: an id and a name. The
            // status, the counts and the entity id the "View" link is built
            // from come from the list endpoint, so the list is read back.
            await loadInstitutions();
            setOpenCreateInstitutionModal(false);
            clearFormData();
        } else {
            console.error("Error creating institution:", response.error);
        }
    };

    return (
        <MainPageLayout>
            <BackButton navigate={navigate} to="/home" />
            <TableCard>
                <PageTitleSection>
                    <PageTitle>Institutions</PageTitle>
                    <CreateButtons
                        backgroundColorHex="#135185"
                        onClick={() => setOpenCreateInstitutionModal(true)}
                    >
                        Create Institution
                    </CreateButtons>
                </PageTitleSection>
                <Table
                    data={institutions}
                    //@ts-ignore
                    columns={columns}
                    loading={loading}
                    loadingText="Loading institutions…"
                    noRowsText="No institutions found"
                />
            </TableCard>
            {openCreateInstitutionModal && (
                <CreateInstitutionModal
                    onSubmit={handleCreateInstitution}
                    onRequestClose={onRequestClose}
                    setFormData={setFormData}
                    formData={formData}
                />
            )}
        </MainPageLayout>
    );
};

export default InstitutionTable;
